import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users, Lock, Check, Loader2 } from "lucide-react";
import { fetchEvents, rsvpEvent } from "@/lib/api";

const ease = [0.22, 1, 0.36, 1];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return "";
  }
};

const EventCard = ({ ev, onRsvp, busy, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7, delay, ease }}
    className={`flex flex-col rounded-2xl border p-6 ${
      ev.rsvped ? "border-[#C5A059]/50 bg-[#C5A059]/5" : "border-[#C5A059]/15 bg-[#070707]"
    }`}
    data-testid={`event-card-${ev.id}`}
  >
    <div className="flex items-center justify-between">
      <span className="rounded-full border border-[#C5A059]/25 px-3 py-1 font-mono text-[0.55rem] uppercase tracking-[0.14em] text-[#C5A059]">
        {ev.type}
      </span>
      <span className="flex items-center gap-1.5 text-[0.65rem] uppercase tracking-[0.14em] text-white/45">
        <CalendarDays size={13} /> {fmtDate(ev.date)}
      </span>
    </div>
    <h4 className="mt-4 font-serif text-2xl font-light text-white">{ev.title}</h4>
    <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-white/55">{ev.description}</p>
    <div className="mt-4 flex flex-wrap items-center gap-4 text-[0.7rem] font-light text-white/45">
      <span className="flex items-center gap-1.5">
        <MapPin size={13} /> {ev.format}
      </span>
      <span className="flex items-center gap-1.5">
        <Users size={13} /> {ev.attendees} attending
      </span>
    </div>
    <button
      onClick={() => !ev.locked && onRsvp(ev.id)}
      disabled={ev.locked || busy}
      className={`mt-5 inline-flex items-center justify-center gap-2 rounded-full border px-6 py-3 text-[0.65rem] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
        ev.locked
          ? "cursor-not-allowed border-white/12 text-white/35"
          : ev.rsvped
          ? "border-[#C5A059] bg-[#C5A059] text-[#050505]"
          : "border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-[#050505]"
      } disabled:opacity-70`}
      data-testid={`event-rsvp-${ev.id}`}
    >
      {busy ? (
        <Loader2 size={13} className="animate-spin" />
      ) : ev.locked ? (
        <Lock size={13} />
      ) : ev.rsvped ? (
        <Check size={13} />
      ) : null}
      {ev.locked ? `Requires ${ev.min_tier}` : ev.rsvped ? "Attending" : "Reserve your place"}
    </button>
  </motion.div>
);

export const MysteryEvents = () => {
  const [events, setEvents] = useState(undefined); // undefined loading, null locked, [] ok
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      setEvents(await fetchEvents());
    } catch (err) {
      if (err?.response?.status === 403) setEvents(null);
      else setEvents([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const onRsvp = async (id) => {
    setBusyId(id);
    try {
      const res = await rsvpEvent(id);
      setEvents((prev) =>
        prev.map((e) =>
          e.id === id
            ? { ...e, rsvped: res.rsvped, attendees: e.attendees + (res.rsvped ? 1 : -1) }
            : e
        )
      );
      toast.success(res.rsvped ? "Your place is reserved." : "Reservation released.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reserve your place.");
    } finally {
      setBusyId(null);
    }
  };

  // Private gatherings are unseen to tiers without access; higher-tier events are hidden too.
  if (events === null) return null;
  const visible = Array.isArray(events) ? events.filter((e) => !e.locked) : [];
  if (Array.isArray(events) && visible.length === 0) return null;

  return (
    <div className="mb-20" data-testid="mystery-events">
      <div className="flex items-center gap-3">
        <CalendarDays size={16} className="text-[#C5A059]" />
        <p className="overline text-[0.6rem]">The Gatherings — Actual Events</p>
      </div>

      {events === undefined && (
        <p className="mt-6 font-serif text-lg font-light italic text-[#C5A059] animate-pulse">
          The calendar unveils…
        </p>
      )}

      {visible.length > 0 && (
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          {visible.map((ev, i) => (
            <EventCard key={ev.id} ev={ev} onRsvp={onRsvp} busy={busyId === ev.id} delay={i * 0.05} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MysteryEvents;
